import React, { useState } from 'react';
import {
  BodyText,
  Button,
  FlexRow,
  Header2Text,
  Header4Text,
  RadioList,
  SpaceWrapper,
  Grid,
  SmallBodyText,
  TextInput,
  Currency,
} from 'ui-toolkit';
import { StyledConfirmEscrowModalWindow } from '../StyledConfirmEscrowModalWindow';
import { IConfirmTransaction } from '../../../../../../types/order/escrow/IConfirmTransaction';
import { IEscrowFileCompleteData } from '../../../../../../types/order/escrow/IEscrowFileCompleteData';
import { FormikTouched } from 'formik';
import { SpaEscrowSubmitButton } from '../../spa/SpaEscrowSubmitButton';

interface IConfirmationEscrowModalProps {
  entriesToConfirm: IConfirmTransaction[];
  onClose: () => void;
  handleSubmit: (formData: IEscrowFileCompleteData, touched: FormikTouched<IEscrowFileCompleteData>) => Promise<void>;
}

const determineIfValid = (
  currentEntry: IConfirmTransaction,
  accountConfirmationText: string | null,
  entryConfirmed: boolean
): boolean => {
  return (
    entryConfirmed &&
    (currentEntry.heading === 'ACH Transfers' ? currentEntry.account?.accountNumber : currentEntry.accountNumber) ===
      accountConfirmationText
  );
};

export const SpaConfirmEscrowModal: React.FC<IConfirmationEscrowModalProps> = ({
  entriesToConfirm,
  onClose,
  handleSubmit,
}) => {
  const [entryIndex, setEntryIndex] = useState(0);
  const currentEntry = entriesToConfirm[entryIndex];
  const [entryConfirmed, setEntryConfirmed] = useState(false);
  const [accountConfirmationText, setAccountConfirmationText] = useState<string | null>('');
  const isValid = determineIfValid(currentEntry, accountConfirmationText, entryConfirmed);

  const beneficiary = currentEntry.heading === 'Wire Transfers' ? currentEntry.beneficiary : currentEntry.account?.name;

  const nextEntry = () => {
    setEntryConfirmed(false);
    setAccountConfirmationText('');
    setEntryIndex(entryIndex + 1);
  };

  const entryHeader = currentEntry.heading;
  const thisTypeList = entriesToConfirm.filter((t) => t.heading === currentEntry.heading);
  const thisTypeIndex = thisTypeList.indexOf(currentEntry);
  return (
    <>
      <StyledConfirmEscrowModalWindow uitype={'fit'} onClose={onClose}>
        <SpaceWrapper topSpace={'none'}>
          <Header2Text>Confirm some details</Header2Text>
          <Header4Text>
            {entryHeader} {thisTypeIndex + 1} of {thisTypeList.length}
          </Header4Text>
        </SpaceWrapper>
        <SpaceWrapper bottomSpace={'none'}>
          <FlexRow>
            <BodyText>Have you confirmed the disbursement information?</BodyText>
          </FlexRow>
        </SpaceWrapper>
        <SpaceWrapper topSpace={'none'} bottomSpace={'none'}>
          <Grid columnWidth={`1fr 2fr`}>
            <RadioList
              orientation={'horizontal'}
              name={`confirm`}
              value={entryConfirmed ? 'Yes' : 'No'}
              onChange={(value) => setEntryConfirmed(value.currentTarget.value === 'Yes')}
              options={[
                { label: 'Yes', value: 'Yes' },
                { label: 'No', value: 'No' },
              ]}
            />
            <div />
          </Grid>
        </SpaceWrapper>
        <SpaceWrapper>
          <FlexRow>
            <SmallBodyText>Beneficiaries:</SmallBodyText>
            <SmallBodyText>{beneficiary}</SmallBodyText>
          </FlexRow>
        </SpaceWrapper>
        <SpaceWrapper>
          <FlexRow>
            <SmallBodyText>Amount:</SmallBodyText>
            <Currency value={currentEntry.amountCents} valueIsCents />
          </FlexRow>
        </SpaceWrapper>
        <SpaceWrapper>
          <FlexRow>
            <TextInput
              label={'Re-enter the beneficiary account number'}
              value={accountConfirmationText}
              placeholder={'Account Number'}
              onChange={(value) => setAccountConfirmationText(value)}
            />
          </FlexRow>
        </SpaceWrapper>
        <hr />
        <FlexRow justifyContent={'flex-end'}>
          {entryIndex < entriesToConfirm.length - 1 ? (
            <>
              <BodyText>
                Next: {entryIndex + 2} of {entriesToConfirm.length}
              </BodyText>
              <Button type="button" intent={'primary'} disabled={!isValid} onClick={nextEntry}>
                Next
              </Button>
            </>
          ) : (
            <SpaEscrowSubmitButton onFormSubmit={handleSubmit} disabled={!isValid} />
          )}
        </FlexRow>
      </StyledConfirmEscrowModalWindow>
    </>
  );
};
